import React, { useState, useEffect, useRef, useCallback } from "react";

import { TableComponent } from "./TableComponent";
import { Message, Modal } from "../../BP2Components";
import { generateGUID } from "../../../util/Scripts";
import { usePrevious } from "../../../util/customHooks";
import { getListV2 } from "../../../services/api/Api";
import { getListV3, getListV3WithFilter, getListFilters } from "../../../services/restAPI/ListsV2";
import { QRCodeSVG } from "qrcode.react";
import { useIntl } from "react-intl";
import { useFormV3 } from "../../../context/FormV3Context/FormV3Context";

import TableFilter from "../../Widgets/TableWidget/TableFilter";

import "./table.less";
import { useTableListContext } from "../../../context/TableListContext/TableListContext";
import { FormV3InModal } from "../../Widgets/FormsV3/FormV3InModal/FormV3InModal";
import { setUrlTo } from "../../FormsV2/GeneralScripts";
import { setBackUrl } from "../../../appRedux/actions/Auth";
import { useDispatch } from "react-redux";
import { handlePocResponse } from "../../../util/pocResponseHandler";
import { persistor } from "../../../appRedux/store/Store";

const modalActions = {
   FORM_ACTION: "FormAction",
   QR_CODE: "QR-Code",
};

const isEmptyFilterValue = (value) =>
   value === false || value === "" || value === null || value === undefined;

export const TableController = ({
   listId,
   itemsPerPage = 10,
   showPager = true,
   splitForPages = false,
   customGUID,
   preparedRows = [],
   withCheckbox = false,
   showListFilters = true,
   defaultFilterIdFromSettings = false,
   allowInlineEdit = false,
}) => {
   const intl = useIntl();
   const { tableRefreshList, setTableRefreshList } = useFormV3();
   const [GUID, setGUID] = useState(customGUID);
   const [page, setPage] = useState(1);
   const dispatch = useDispatch();

   const [actions, setActions] = useState();
   const [loadingPage, setLoadingPage] = useState(false);
   const [loadingTable, setLoadingTable] = useState(false);
   const [totalItems, setTotalItems] = useState();
   const [currentAction, setCurrentAction] = useState();
   const [addItemModalOpen, setAddItemModalOpen] = useState(false);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [filters, setFilters] = useState({});
   const [addItemFormId, setAddItemFormId] = useState(null);
   const [pocData, setPocData] = useState({});
   const [presenterFormId, setPresenterFormId] = useState(null);

   const previousData = usePrevious({ page, itemsPerPage, listId });

   const {
      activeFilter,
      columnList,
      setColumnList,
      rowList,
      setRowList,
      setTotalElements,
      activeTab,
      setListFilters,
      setEditedListFilters,
      setListGroupsForMassActions,
      groupsIsChanged,
      setGroupsIsChanged,
   } = useTableListContext();

   const [lastTab, setLastTab] = useState(activeTab);
   const type = activeTab === 2 ? "group" : "profile";

   const activeTabRef = useRef(activeTab);
   useEffect(() => {
      activeTabRef.current = activeTab;
   }, [activeTab]);

   const initialLoadDoneByListIdRef = useRef({});
   useEffect(() => {
      initialLoadDoneByListIdRef.current[String(listId)] = false;
   }, [listId]);

   const getEffectiveFilterId = () => {
      const hasDefaultFilter = !isEmptyFilterValue(defaultFilterIdFromSettings);

      if (hasDefaultFilter && !showListFilters) {
         return defaultFilterIdFromSettings;
      }

      return activeFilter;
   };

   const pendingFilterLoadTimeoutRef = useRef(null);

   useEffect(() => {
      if (!customGUID) {
         setGUID(generateGUID("table"));
      }
   }, []);

   const stopLoading = () => {
      setLoadingPage(false);
      setLoadingTable(false);
   };

   const handleListV3Response = (response) => {
      const rawGroups = response.data?.meta?.groups;
      const groupsArray = Array.isArray(rawGroups) ? rawGroups : Object.values(rawGroups ?? {});

      if (activeTabRef.current === 2) {
         setListFilters(groupsArray);
         setEditedListFilters(groupsArray);
      }
      setListGroupsForMassActions(groupsArray);

      setTotalElements(response.data.meta.totalElements);
      setColumnList(response.data.columns.filter((column) => column.options?.visible));
      setRowList(response.data.data);
      setActions(response.data.actions);
      setTotalItems(response.data.meta.totalElements);
      setAddItemFormId(response.data.meta.global?.addItemFormId);
      setPresenterFormId(
         response.data.meta.global?.presenterFormId ?? response.data.meta.displayFormChildId,
      );
   };

   const handleListV3Error = (error) => {
      console.error("Bad list request:", error);
      Message.error(intl.formatMessage({ id: "list.errorLoadingTableData" }));
   };

   const handleListV3Finally = () => {
      initialLoadDoneByListIdRef.current[String(listId)] = true;
      stopLoading();
   };

   const getInitialFiltersFromURL = (currentListId) => {
      const urlParams = new URLSearchParams(window.location.search);
      try {
         const initialFilters = JSON.parse(decodeURIComponent(urlParams.get("listFilters")))?.[
            GUID
         ];
         if (initialFilters) {
            setFilters({ [currentListId]: initialFilters });
            return { [currentListId]: initialFilters }?.[currentListId];
         }
      } catch (error) {
         console.error("Invalid filter format in URL parameters", error);
         Message.error(intl.formatMessage({ id: "list.invalidFilterFormatInURLParameters" }));
      }
      return {};
   };

   const loadTableData = async (refreshTableComponent, currentType) => {
      if (preparedRows?.length > 0) {
         setRowList(preparedRows);
         setTotalItems(preparedRows.length);
         return;
      }

      if (listId && (Number.isInteger(+listId) || listId.length > 10)) {
         setLoadingPage(true);
         if (refreshTableComponent) {
            setLoadingTable(true);
         }
      }

      if (Number.isInteger(+listId) && listId.length < 10) {
         getListV2(
            listId,
            page,
            itemsPerPage,
            Object.keys(filters).length === 0 ? getInitialFiltersFromURL(listId) : filters[listId],
         )
            .then((response) => {
               setColumnList(response.data.default.columns);
               setRowList(response.data.default.data);
               setTotalItems(response.data.default.meta.totalItemsCount);
            })
            .catch((error) => {
               console.error("Bad list request:", error);
               Message.error(intl.formatMessage({ id: "list.errorLoadingTableData" }));
            })
            .finally(() => {
               stopLoading();
            });

         return;
      }

      if (listId && listId.length > 10) {
         const tabsIsChange = activeTab !== lastTab;

         const effectiveFilterId = getEffectiveFilterId();
         const hasEffectiveFilter = !isEmptyFilterValue(effectiveFilterId);

         if (hasEffectiveFilter && !tabsIsChange) {
            if (
               !isEmptyFilterValue(defaultFilterIdFromSettings) &&
               effectiveFilterId === defaultFilterIdFromSettings
            ) {
               let profiles;
               try {
                  const res = await getListFilters(listId);
                  profiles = res?.data ?? [];
               } catch (err) {
                  handleListV3Error(err);
                  handleListV3Finally();
                  return;
               }
               const filterExists = profiles.some(
                  (p) => String(p.id) === String(effectiveFilterId),
               );
               if (!filterExists) {
                  getListV3(
                     listId,
                     page,
                     itemsPerPage,
                     Object.keys(filters).length === 0
                        ? getInitialFiltersFromURL(listId)
                        : filters[listId],
                  )
                     .then(handleListV3Response)
                     .catch(handleListV3Error)
                     .finally(handleListV3Finally);
                  return;
               }
            }

            getListV3WithFilter(listId, page, itemsPerPage, effectiveFilterId, currentType)
               .then(handleListV3Response)
               .catch(handleListV3Error)
               .finally(handleListV3Finally);

            return;
         }

         getListV3(
            listId,
            page,
            itemsPerPage,
            Object.keys(filters).length === 0 ? getInitialFiltersFromURL(listId) : filters[listId],
         )
            .then(handleListV3Response)
            .catch(handleListV3Error)
            .finally(handleListV3Finally);

         return;
      }
   };

   const { filter } = TableFilter({ GUID, listId, filters, setFilters, loadTableData });

   const onPageChange = (newPage) => {
      if (newPage !== page) setPage(newPage);
   };

   useEffect(() => {
      setLastTab(activeTab);
      loadTableData(true, type);
   }, [activeTab]);

   useEffect(() => {
      if (activeTab !== lastTab) return;

      if (pendingFilterLoadTimeoutRef.current) {
         clearTimeout(pendingFilterLoadTimeoutRef.current);
         pendingFilterLoadTimeoutRef.current = null;
      }

      const isFilterTemporarilyCleared = isEmptyFilterValue(activeFilter);

      if (!isFilterTemporarilyCleared) {
         loadTableData(true, type);
         return;
      }

      pendingFilterLoadTimeoutRef.current = setTimeout(() => {
         if (isEmptyFilterValue(activeFilter)) {
            loadTableData(true, type);
         }
         pendingFilterLoadTimeoutRef.current = null;
      }, 150);
   }, [activeFilter]);

   useEffect(() => {
      return () => {
         if (pendingFilterLoadTimeoutRef.current) {
            clearTimeout(pendingFilterLoadTimeoutRef.current);
            pendingFilterLoadTimeoutRef.current = null;
         }
      };
   }, []);

   useEffect(() => {
      if (groupsIsChanged) {
         loadTableData(true, type);
         setGroupsIsChanged(false);
      }
   }, [groupsIsChanged]);

   useEffect(() => {
      if (tableRefreshList) {
         const reducedRefreshObject = {};
         Object.keys(tableRefreshList).forEach((key) => {
            if (key === listId) {
               loadTableData(true, type);
            } else {
               reducedRefreshObject[key] = key;
            }
         });
         setTableRefreshList(reducedRefreshObject);
      }
   }, [JSON.stringify(tableRefreshList)]);

   useEffect(() => {
      if (previousData && page !== previousData?.page) {
         loadTableData(false, type);
      }
   }, [page]);

   useEffect(() => {
      if (
         previousData &&
         (previousData?.listId !== listId || previousData?.itemsPerPage !== itemsPerPage)
      ) {
         loadTableData(true, type);
      }
   }, [listId, itemsPerPage]);

   const defaultFilterIdRef = useRef(defaultFilterIdFromSettings);
   useEffect(() => {
      if (defaultFilterIdRef.current !== defaultFilterIdFromSettings) {
         defaultFilterIdRef.current = defaultFilterIdFromSettings;
         loadTableData(true, type);
      }
   }, [defaultFilterIdFromSettings]);

   const showListFiltersRef = useRef(showListFilters);
   useEffect(() => {
      if (showListFiltersRef.current !== showListFilters) {
         showListFiltersRef.current = showListFilters;
         loadTableData(true, type);
      }
   }, [showListFilters]);

   const handleAddRecordButton = () => {
      setAddItemModalOpen(true);
   };

   const [isQrModalOpen, setIsQrModalOpen] = useState(false);

   const onCustomEventImplRef = useRef(null);
   onCustomEventImplRef.current = async (customEvent) => {
      if (customEvent?.type === "listReloadRequest") {
         loadTableData(true, type);
         return;
      }
      if (!customEvent?.data?.actionName && !!customEvent?.data?.url) {
         await handlePocResponse(customEvent.data.url, {
            rowId: customEvent.rowData.ID,
            callbacks: {
               onOpenForm: (data) => {
                  setIsModalOpen(true);
                  setPocData(data);
               },
               onRefreshList: () => window.location.reload(),
               onOpenUrl: (url) => setUrlTo(url),
               onOpenWorkflow: (instanceId, rowId) => {
                  setUrlTo(`/cases/${rowId}?recordId=${rowId}&instanceId=${instanceId}`);
               },
            },
         });
      }
      if (customEvent?.type === "Action" && !!customEvent?.data?.actionName) {
         if (customEvent?.data?.name === modalActions.QR_CODE) {
            setIsQrModalOpen(true);
            setCurrentAction({ type: modalActions.QR_CODE, customEvent: customEvent });
         } else if (customEvent?.data?.actionName === modalActions.FORM_ACTION) {
            setIsModalOpen(true);
            setCurrentAction({
               type: modalActions.FORM_ACTION,
               formId: customEvent?.data?.args?.id,
               dataId: customEvent.rowData.UID ? customEvent.rowData.UID : customEvent.rowData.ID,
            });
         } else if (customEvent?.data?.actionName === "LegacyWorkflowAction") {
            const backURL = window.location.pathname;
            dispatch(setBackUrl(backURL));
            const recordId = customEvent?.rowData?.ID;
            const actionName = customEvent?.data?.actionName;
            await persistor.flush();
            setUrlTo(
               `/cases/${recordId}?&recordId=${recordId}&listId=${listId}&v3ActionName=${actionName}`,
            );
         }
      }
   };

   const onCustomEvent = useCallback((customEvent) => {
      return onCustomEventImplRef.current?.(customEvent);
   }, []);

   const isObject = (data) => {
      return data && typeof data === "object" && Object.keys(data).length > 0;
   };

   return (
      <>
         <TableComponent
            listId={listId}
            actions={actions}
            withCheckbox={withCheckbox}
            colData={columnList}
            page={page}
            rowData={rowList}
            onPageChange={onPageChange}
            itemsPerPage={itemsPerPage}
            loadingPage={loadingPage}
            loadingTable={loadingTable}
            totalItems={totalItems}
            showPager={showPager}
            onCustomEvent={onCustomEvent}
            addRecordBtn={!!addItemFormId}
            formId={allowInlineEdit ? presenterFormId : null}
            addRecordCallback={handleAddRecordButton}
            splitForPages={splitForPages}
            filter={filter}
         ></TableComponent>

         {addItemModalOpen ? (
            <FormV3InModal
               formId={addItemFormId}
               saveCallback={() => {
                  setAddItemModalOpen(!addItemModalOpen);
               }}
               setIsOpen={() => setAddItemModalOpen(!addItemModalOpen)}
               isOpen={addItemModalOpen}
            />
         ) : null}

         {(currentAction && currentAction?.type === modalActions.FORM_ACTION) ||
         isObject(pocData) ? (
            <FormV3InModal
               formId={currentAction?.formId ?? pocData?.id}
               dataId={currentAction?.dataId}
               isOpen={isModalOpen}
               setIsOpen={() => setIsModalOpen(false)}
               pocData={pocData}
               setPocData={setPocData}
            />
         ) : null}

         {currentAction && currentAction?.type === modalActions.QR_CODE && (
            <Modal
               width={430}
               height={430}
               isOpen={isQrModalOpen}
               bodyStyles={{ margin: "0px" }}
               onCancel={() => setIsQrModalOpen(false)}
               onCancelButtonStyle={{ color: "white" }}
               topBarStyles={{ backgroundColor: "#115622", borderBottom: "1px solid #115622" }}
               customCloseIcon={<span className="material-symbols-outlined">close</span>}
            >
               {currentAction?.customEvent?.rowData?.UID ? (
                  <div className="main-qr-code-container">
                     <div className="main-qr-code-container__qr-code-padding">
                        <QRCodeSVG
                           width={300}
                           height={300}
                           value={currentAction?.customEvent?.rowData?.UID}
                        />
                     </div>
                  </div>
               ) : (
                  <div className="main-qr-code-container">
                     <span style={{ color: "white" }}>brak danych do wygenerowania kodu!</span>
                  </div>
               )}
            </Modal>
         )}
      </>
   );
};
